import MightyHostsBanner from 'elements/resourceCenter/MightyHostsBanner';
import React from 'react';
import { graphql, PageProps } from 'gatsby';
import Layout from 'src/elements/layout/layout';
import { IGatsbyImageData } from 'gatsby-plugin-image';
import { TemplateBody, TemplateContainer, TemplateContent, TemplateTitle, TemplateTitleBackgroundMesh, TemplateTitleBackgroundPoligon, TemplateTitleCard } from './resourceCenter.styles';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import RecommendationsCarousel from 'organisms/articles/RecommendationsCarousel';
import { Attachments, formatElementFromHtmlAst } from 'src/functions/formatElement.htmlAst';

import ResourceCenterMenu from 'src/organisms/articles/resourceCenter/resourceCenter.menu';
import SocialMediaTopBarLinks from 'src/organisms/layout/SocialMediaTopBarLinks';
import calculateEstimatedReadingTime from 'src/functions/calculateEstimatedReadingTime';

interface MarkupValue {
  type: 'text';
  value: string;
}

interface MarkupElement {
  type: 'element';
  tagName: 'a' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'strong' | 'ul' | 'ol' | 'li' | 'img';
  properties: {
    href?: string;
    src?: string;
  }
  children: (MarkupElement | MarkupValue)[]
}


interface ResourceCenterProps extends PageProps {
  data: {
    allContentfulResource: {
      edges: {
        node: {
          title: string;
          slug: string;
          category: string;
          subCategory: string;
          heroImage: { gatsbyImageData: IGatsbyImageData };
        }
      }[]
    }
    contentfulResource: {
      id: string;
      title: string;
      slug: string;
      seoTitle?: string;
      seoDescription?: string;
      author?: string;
      contentful_id: string;
      description: { description: string; }
      heroImage?: {
        gatsbyImageData: IGatsbyImageData;
        file?: {
          url: string;
        }
      };
      attachments: Attachments
      body: {
        internal: { type: string }
        childMarkdownRemark: {
          wordCount?: {
            words?: number
          }
          htmlAst: {
            type: string;
            children: MarkupElement[]
          }
        }
      }
    }
  }
}

const ResourceCenterTemplate = (props: ResourceCenterProps): JSX.Element => {
  const articleData = props.data.contentfulResource;

  return (
    <Layout
      helmet={{
        title: articleData.seoTitle || articleData.title,
        description: articleData.seoDescription || articleData.description?.description,
        previewImageURL: props.data.contentfulResource.heroImage?.file.url,
        author: articleData.author,
        estimatedReadingTime: calculateEstimatedReadingTime(articleData.body.childMarkdownRemark?.wordCount?.words)
      }}
      topBarLinkSection={<SocialMediaTopBarLinks path={`/resources/${articleData.slug}`} title={articleData.title}/>}
      location={props.location}
    >
      <TemplateContainer>
        <ResourceCenterMenu />
        <TemplateBody>
          <TemplateTitle>
            <TemplateTitleCard>
              <Heading
                text={articleData.title}
                size="3.5em"
                sizeSubLg="2.5em"
                sizeXs="1.5em"
                align="center"
              />
              <Paragraph
                text={articleData.description?.description || ''}
                size="2em"
                sizeSubLg="1.25em"
                sizeXs="1em"
                weight="light"
                align="center"
              />
            </TemplateTitleCard>
            <TemplateTitleBackgroundPoligon>
              <svg xmlns="http://www.w3.org/2000/svg" width="283" height="313" viewBox="0 0 283 313" fill="none">
                <path d="M63.9576 0.828146L0.716603 149.215L58.975 265.657L220.046 312.803L282.515 153.814L227.554 43.9639L63.9576 0.828146Z" fill="url(#paint0_linear)" />
                <defs>
                  <linearGradient id="paint0_linear" x1="107.808" y1="-1.03944" x2="221.858" y2="311.896" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#626348" />
                    <stop offset="1" stopColor="#80D1CD" />
                  </linearGradient>
                </defs>
              </svg>
            </TemplateTitleBackgroundPoligon>
            <TemplateTitleBackgroundMesh>
              <svg xmlns="http://www.w3.org/2000/svg" height="440" viewBox="0 0 309 365" fill="none">
                <g opacity="0.5">
                  <path d="M213.135 39.0988L84.9611 85.2824L39.7056 191.635L91.8838 324.042L226.502 271.121L269.196 170.789L213.135 39.0988Z" stroke="url(#paint0_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M207.562 52.6202L92.1605 94.7255L51.1791 191.033L97.7632 310.652L218.979 262.437L257.641 171.58L207.562 52.6202Z" stroke="url(#paint1_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M201.991 66.1419L99.3606 104.169L62.6533 190.432L103.643 297.263L211.457 253.752L246.087 172.372L201.991 66.1419Z" stroke="url(#paint2_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M195.746 81.2456L106.29 114.247L74.36 189.284L110.197 282.289L204.168 244.521L234.291 173.731L195.746 81.2456Z" stroke="url(#paint3_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M189.5 96.3495L113.219 124.325L86.0659 188.136L116.75 267.316L196.877 235.289L222.494 175.09L189.5 96.3495Z" stroke="url(#paint4_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M182.918 112.245L120.014 134.72L97.8886 186.715L123.64 251.551L189.704 225.785L210.576 176.733L182.918 112.245Z" stroke="url(#paint5_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M176.673 127.35L126.943 144.799L109.595 185.568L130.193 236.578L182.413 216.554L198.779 178.093L176.673 127.35Z" stroke="url(#paint6_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M171.101 140.871L134.143 154.243L121.069 184.967L136.073 223.188L174.891 207.87L187.225 178.885L171.101 140.871Z" stroke="url(#paint7_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M165.758 155.424L176.034 178.822L167.486 198.911L142.855 209.246L132.659 184.091L140.724 165.14L165.758 155.424Z" stroke="url(#paint8_linear)" strokeWidth="2" strokeLinejoin="round" />
                  <path d="M159.542 169.606L164.103 180.5L160.08 189.954L148.764 194.934L144.25 183.217L148.045 174.299L159.542 169.606Z" stroke="url(#paint9_linear)" strokeWidth="2" strokeLinejoin="round" />
                </g>
                <defs>
                  <linearGradient id="paint0_linear" x1="214.649" y1="39.7433" x2="93.3984" y2="324.687" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint1_linear" x1="208.923" y1="53.1992" x2="99.1237" y2="311.231" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint2_linear" x1="203.198" y1="66.6553" x2="104.85" y2="297.776" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint3_linear" x1="196.799" y1="81.6934" x2="111.249" y2="282.737" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint4_linear" x1="190.399" y1="96.7318" x2="117.648" y2="267.698" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint5_linear" x1="183.663" y1="112.562" x2="124.385" y2="251.867" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint6_linear" x1="177.263" y1="127.601" x2="130.784" y2="236.829" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint7_linear" x1="171.538" y1="141.057" x2="136.51" y2="223.374" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint8_linear" x1="165.475" y1="155.303" x2="142.573" y2="209.126" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                  <linearGradient id="paint9_linear" x1="159.414" y1="169.551" x2="148.636" y2="194.88" gradientUnits="userSpaceOnUse">
                    <stop stopColor="#FE007C" />
                    <stop offset="1" stopColor="#261447" />
                  </linearGradient>
                </defs>
              </svg>
            </TemplateTitleBackgroundMesh>
          </TemplateTitle>
          <TemplateContent>
            {articleData.body.childMarkdownRemark.htmlAst.children.map((child, index) => formatElementFromHtmlAst(child, `${index}`, articleData.attachments))}
          </TemplateContent>
        </TemplateBody>

        <RecommendationsCarousel posts={
          props.data.allContentfulResource.edges
            .filter(e =>  !!e.node.heroImage?.gatsbyImageData && e.node.slug !== articleData.slug)
            .map((edge) => ({
              title: edge.node.title,
              slug: edge.node.slug,
              image: edge.node.heroImage.gatsbyImageData
            }))}
        />

        <MightyHostsBanner />
      </TemplateContainer>
    </Layout>
  )
}


export default ResourceCenterTemplate

export const pageQuery = graphql`
  query ResourceCenterBySlug($slug: String!, $category: String!) {
    contentfulResource(slug: { eq: $slug }) {
      id
      title
      slug
      contentful_id
      subCategory
      category
      seoTitle
      seoDescription
      author
      heroImage {
        file {
          url
        }
        gatsbyImageData(width: 960, layout: CONSTRAINED, placeholder: BLURRED)
      }
      description {
        description
      }
      attachments {
        ...ComparisonTable
      }
      body  {
        internal {
          type
        }
        childMarkdownRemark {
          htmlAst
          wordCount {
            words
          }
        }
      }
    }
    allContentfulResource(limit: 20, filter: {category: {eq: $category}}) {
      edges {
        node {
          id
          title
          slug
          contentful_id
          subCategory
          category
          heroImage {
            gatsbyImageData(width: 750, layout: CONSTRAINED, placeholder: BLURRED)
          }
        }
      }
    }
  }
`
